import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PasswordField from "../../components/ui/PasswordField/PasswordField";
import { ApiError } from "../../lib/api";
import { authApi } from "../../lib/auth";

type ResetStatus = "ready" | "success" | "error";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<ResetStatus>("ready");
  const [message, setMessage] = useState<string | null>(null);
  const tokenRef = useRef(searchParams.get("token")?.trim() ?? "");

  useEffect(() => {
    tokenRef.current = searchParams.get("token")?.trim() ?? "";
    if (!tokenRef.current) {
      setStatus("error");
      setMessage("This reset link is missing a token. Request a new password reset email and try again.");
    }
  }, [searchParams]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!tokenRef.current || isSubmitting) {
      return;
    }

    if (password.length < 8) {
      setStatus("error");
      setMessage("Your new password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setStatus("error");
      setMessage("Passwords must match.");
      return;
    }

    setIsSubmitting(true);
    setStatus("ready");
    setMessage(null);

    try {
      await authApi.csrf();
      await authApi.resetPassword({
        token: tokenRef.current,
        newPassword: password,
      });
      setStatus("success");
      setMessage("Your password has been reset. You can now log in with your new password.");
    } catch (error) {
      setStatus("error");

      if (error instanceof ApiError && error.status === 410) {
        setMessage("This reset link has expired. Request a new password reset email and try again.");
      } else if (error instanceof ApiError && error.status === 401) {
        setMessage("This reset link is invalid or has already been used.");
      } else {
        setMessage("We could not reset your password right now. Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-myteengig-background px-6 py-16">
      <div className="mx-auto max-w-2xl overflow-hidden rounded-[32px] border border-myteengig-border bg-white shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
        <div className="bg-[linear-gradient(135deg,rgba(249,115,22,0.08),rgba(37,99,235,0.08))] px-8 py-10">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-orange-600">Reset Password</p>
          <h1 className="mt-3 text-3xl font-bold text-myteengig-text-primary">Choose a new password</h1>
          <p className="mt-4 max-w-xl text-base text-myteengig-text-muted">
            Use the secure link from your email to set a new password for your MyTeenGig account.
          </p>
        </div>

        <div className="px-8 py-8">
          {message ? (
            <div
              className={`rounded-2xl border p-5 text-sm leading-6 ${
                status === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : status === "error"
                    ? "border-red-200 bg-red-50 text-red-700"
                    : "border-myteengig-border bg-myteengig-background/60 text-myteengig-text-muted"
              }`}
            >
              {message}
            </div>
          ) : null}

          {status !== "success" ? (
            <form onSubmit={(event) => void onSubmit(event)} className="mt-6 space-y-4">
              <div>
                <label htmlFor="reset-password" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                  New password
                </label>
                <PasswordField
                  id="reset-password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  maxLength={72}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                  placeholder="Enter a new password"
                />
              </div>

              <div>
                <label htmlFor="reset-password-confirm" className="mb-2 block text-sm font-medium text-myteengig-text-primary">
                  Confirm new password
                </label>
                <PasswordField
                  id="reset-password-confirm"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  maxLength={72}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="w-full rounded-xl border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary outline-none focus:border-myteengig-primary"
                  placeholder="Re-enter your new password"
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <Link to="/login" className="text-sm font-medium text-myteengig-primary hover:underline">
                  Back to login
                </Link>
                <button
                  type="submit"
                  disabled={isSubmitting || !tokenRef.current}
                  className="inline-flex items-center rounded-full bg-myteengig-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-myteengig-primary-hover disabled:cursor-not-allowed disabled:bg-myteengig-secondary-hover"
                >
                  {isSubmitting ? "Resetting..." : "Reset password"}
                </button>
              </div>
            </form>
          ) : (
            <div className="mt-8">
              <button
                type="button"
                onClick={() => navigate("/login", { replace: true })}
                className="inline-flex items-center rounded-full bg-myteengig-primary px-5 py-3 text-sm font-semibold text-white transition hover:bg-myteengig-primary-hover"
              >
                Go to login
              </button>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ResetPasswordPage;
